import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Container, IconButton, SwipeableDrawer, Tooltip, Typography } from '@mui/material';
import { dispatch, useSelector } from '../../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../../routes/paths';
import useSettings from '../../../../../hooks/useSettings';
import Page from '../../../../../components/Page';
import HeaderBreadcrumbs from '../../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../../components/table/DataGridTable';
import { fCurrency } from '../../../../../utils/formatNumber';
import { getPurchaseNotes, updatePurchaseNotes, uploadPurchaseNotesDocuments } from '../../../../../redux/slices/purchaseRedux/purchaseNotesRedux';
import { BASE_URL, COMPANY_DATA_FOR_PDF } from '../../../../../config';
import { accessVerify, capitalize, handleNumber, removeUnwantedStockBarCodeList, safeJSONParse, utcMoment } from '../../../../../utils/common';
import PermissionRequired from '../../../../errorPages/permissionRequired';
import { PictureAsPdf } from '@material-ui/icons';
import CashFlowImageUploader, { isImageDocument } from '../../../finance/cashflow/components/cashflowImageUploader';
import ImageViewer from 'react-simple-image-viewer';
import { imageUpload } from '../../../../../inteceptor';
import Iconify from '../../../../../components/Iconify';
import PdfViewerDrawer from '../../../../../components/pdfViewerDrawer';

export default function GrnToBePaid() {
  const { themeStretch } = useSettings();
  const [enteredPricing, setEnteredPricing] = useState([]);
  const [rowSelection, setRowSelection] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [documentGallery, setDocumentGallery] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedDataObj, setSelectedDataObj] = useState({ linkedDocuments: [] });
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);
  const [workflowLog, setWorkflowLog] = useState(null);
  const imageGallery = documentGallery?.filter(isImageDocument) || [];

  const { loggedInUser } = useSelector((state) => state?.user);
  const { data, addData } = useSelector((state) => state?.purchaseNotes);
  const { selectedLocation } = useSelector((state) => state.location);
  const cutoffDate = new Date('2026-01-06T00:00:00Z');

  useEffect(() => {
    if (selectedLocation?.id) {
      dispatch(getPurchaseNotes());
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    setEnteredPricing(
      data
        ?.filter((value) => {
          const paymentMismatch = Math.round(value?.paymentforreceivings) !== Math.round(value?.paidamount);

          const validStatus = ['RECEIVED', 'PARTIALLY_PAID'].includes(value?.status);

          const locationCondition =
            selectedLocation?.id === 1
              ? true // no location filtering
              : value.locationId == selectedLocation.id;

          return paymentMismatch && validStatus && locationCondition;
        })
        ?.map((value) => ({
          ...value,
          linkedDocuments: value?.linkedDocuments ? safeJSONParse(value?.linkedDocuments)?.map((doc) => doc?.url) : [],
        }))
    );
  }, [data, selectedLocation]);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getPurchaseNotes());
    }
  }, [addData]);

  const totalAmountToBePaid = useMemo(
    () =>
      enteredPricing.reduce((total, value) => {
        const outstandingAmount = handleNumber(value?.paymentforreceivings) - handleNumber(value?.paidamount);
        return total + Math.max(outstandingAmount, 0);
      }, 0),
    [enteredPricing]
  );

  const getItemsForInvoice = (rowData) => {
    if (rowData) {
      const parsedRowData = safeJSONParse(rowData);
      return parsedRowData?.map((value, index) => {
        const jsonData = safeJSONParse(value?.jsonData);
        const pricePerSupplierUnit = (jsonData?.price / jsonData?.purchaseUnitQty) * jsonData?.storeUnitQty;
        return {
          id: index,
          title: jsonData?.FoodName,
          foodId: value?.foodId,
          description: `Final Price is checked on ${jsonData?.formatted_dateTime}.`,
          quantity: jsonData?.purchaseUnitQty,
          storeUnitQty: jsonData?.storeUnitQty + ' ' + jsonData?.unitSymbol,
          price: pricePerSupplierUnit,
        };
      });
    }
  };
  useEffect(() => {
    setDocumentGallery(selectedDataObj?.linkedDocuments);
  }, [selectedDataObj]);

  const parseWorkflowLogs = (rowData) => {
    const rawLog = rowData?.workflowLog;
    if (!rawLog) return [];

    const parsedLog = safeJSONParse(rawLog);
    if (!Array.isArray(parsedLog)) return [];

    return parsedLog.map((log) => ({
      ...log,
      date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
    }));
  };

  const handleDownload = async (row) => {
    setPdfRow({
      invoiceNumber: `INV-${row?.original?.id}`,
      createDate: utcMoment(row?.original?.date).format('DD MMMM yyyy').toString(),
      dueDate: utcMoment(row?.original?.date)
        .add(row?.original?.supplierNumberOfDaysForPayment || 30, 'days')
        .format('DD MMMM yyyy')
        .toString(),
      status: row?.original?.status,
      discount: 0,
      taxes: 0,
      paidAmount: handleNumber(row?.original?.paidamount),
      totalPrice: handleNumber(row?.original?.totalamount) - handleNumber(row?.original?.paidamount),
      subTotalPrice: handleNumber(row?.original?.totalamount),
      topic: 'Purchase Order',
      invoiceTo: {
        name: COMPANY_DATA_FOR_PDF?.name,
        address: row?.original?.locationAddress,
        phone: row?.original?.locationContactNumber,
      },
      invoiceFrom: {
        name: row?.original?.supplierName,
        address: row?.original?.supplierAddress,
        phone: row?.original?.supplierCompanyPhone,
      },
      items: getItemsForInvoice(row?.original?.supplierorder_foodsupplierpricing),
      stockCodes: removeUnwantedStockBarCodeList(safeJSONParse(row?.original?.stockCodes)) || [],
      linkedDocuments: row?.original?.linkedDocuments?.map((value) => value?.replace('https://storage.googleapis.com/bubblemania/', `${BASE_URL}imageViewer?path=`)) || [],
      supplierInvoiceNumber: row?.original?.supplierInvoiceNumber,
    });
    setWorkflowLog(parseWorkflowLogs(row?.original));

    setIsPdfDrawerOpen(true);
  };

  const columns = !enteredPricing?.[0]
    ? []
    : Object.keys(enteredPricing?.[0])
        .map((value) => {
          if (
            value === 'supplierAddress' ||
            value === 'estimatedreceivingdate' ||
            value === 'paymentdate' ||
            value === 'paymentmethod' ||
            value === 'supplierCode' ||
            value === 'supplierCompanyPhone' ||
            value === 'paymentMethod' ||
            value === 'paymentDate' ||
            value === 'estimatedReceivingDate' ||
            value === 'orderedById' ||
            value === 'supplierorder_foodsupplierpricing'
          ) {
            return null;
          } else if (value === 'supplierId') {
            return {
              accessorKey: 'supplierId',
              header: 'Invoice Id',
              Cell: ({ row }) => `INV-${row?.original?.id}`,
            };
          } else if (value === 'totalamount') {
            return {
              accessorKey: 'totalamount',
              header: 'Total Amount',
              Cell: ({ row }) => fCurrency(row?.original?.totalamount),
            };
          } else if (value === 'paidamount') {
            return {
              accessorKey: 'paidamount',
              header: 'Paid Amount',
              Cell: ({ row }) => fCurrency(row?.original?.paidamount),
            };
          } else if (value === 'paymentforreceivings') {
            return {
              accessorKey: 'paymentforreceivings',
              header: 'Payment Amount For Receivings',
              Cell: ({ row }) => fCurrency(row?.original?.paymentforreceivings),
            };
          } else {
            return {
              accessorKey: value,
              header: capitalize(value),
            };
          }
        })
        .filter((value) => value);

  const uploadFiles = (file) => {
    return new Promise((resolve, reject) => {
      imageUpload(
        file,
        `Finance/PurchaseNote_Invoice/Loc_${selectedLocation?.id}/User_${loggedInUser?.id}`,
        (data) => {
          if (data?.status) {
            resolve(data);
          } else {
            reject(new Error('Upload failed'));
          }
        },
        (errorMessage) => {
          console.error('Upload failed:', errorMessage);
          reject(new Error(errorMessage));
        }
      );
    });
  };

  const handleImageUpload = async () => {
    setIsUploading(true);
    for (const file of files) {
      try {
        setFiles((value) => {
          return value.map((fileValue, index) => {
            if (file?.id === fileValue?.id) {
              return {
                ...fileValue,
                isUploading: true,
              };
            }
            return fileValue;
          });
        });

        const data = await uploadFiles(file);

        setFiles((value) => {
          return value.filter((fileValue, index) => file?.id !== fileValue?.id);
        });
        if (data?.status) {
          setSelectedDataObj((prevState) => ({
            ...prevState,
            linkedDocuments: [...prevState.linkedDocuments, data?.url],
          }));
          dispatch(uploadPurchaseNotesDocuments({ supplierorderId: selectedDataObj?.id, url: data?.url }));
        }
      } catch (error) {
        setFiles((value) => {
          return value.map((fileValue, index) => {
            if (file?.id === fileValue?.id) {
              return {
                ...fileValue,
                isUploading: false,
              };
            }
            return fileValue;
          });
        });
      }
    }
    setIsUploading(false);
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  return (
    <Page title={`GRN Payment (GRN To be Paid)`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`GRN Payment (GRN To be Paid)`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Finance',
              href: PATH_DASHBOARD.purchasing.root,
            },
            { name: `GRN Payment (GRN To be Paid)` },
          ]}
          action={[]}
        />

        {accessVerify('FINANCE_GRN_GRN_TO_BE_VIEW') ? (
          <>
            <Card sx={{ m: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: '400' }}>
                    Total Amount To Be Paid
                  </Typography>
                  <Typography variant='h6' sx={{ color: 'red', fontWeight: '500' }}>
                    {fCurrency(totalAmountToBePaid)}
                  </Typography>
                </Box>
              </Box>
            </Card>
            <DataGridTable
              name={'GRN To be Paid'}
              data={enteredPricing}
              column={columns}
              isLoading={false}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              enableRowActions={true}
              renderRowActionItems={(row, closeMenu) => [
                <div style={{ minWidth: '100px' }}>
                  <Tooltip title='Download PDF'>
                    <IconButton
                      color='error'
                      onClick={() => {
                        handleDownload(row);
                      }}
                    >
                      <PictureAsPdf />
                    </IconButton>
                  </Tooltip>
                </div>,
              ]}
            />
          </>
        ) : (
          <PermissionRequired />
        )}
        <SwipeableDrawer
          anchor={'bottom'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '95%',
              height: '95%',
              marginLeft: '2.5%',
              borderRadius: '10px',
            },
          }}
        >
          <CashFlowImageUploader
            files={files}
            setFiles={setFiles}
            handleImageUpload={handleImageUpload}
            documentGallery={documentGallery}
            openImageViewer={openImageViewer}
            isUploading={isUploading}
          />
          {isViewerOpen && <ImageViewer src={imageGallery} currentIndex={currentImage} disableScroll={false} closeOnClickOutside={true} onClose={closeImageViewer} />}

          {selectedDataObj?.linkedDocuments?.length > 0 && selectedDataObj?.status === 'DRAFT' && (
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={() => {
                setSelectedDataObj((prevState) => {
                  return {
                    ...prevState,
                    status: 'Updated',
                  };
                });
                dispatch(
                  updatePurchaseNotes({
                    id: selectedDataObj?.id,
                    status: 'PROCESSING',
                  })
                );
              }}
            >
              Upload And continue process
            </Button>
          )}
        </SwipeableDrawer>

        <PdfViewerDrawer open={isPdfDrawerOpen} onClose={() => setIsPdfDrawerOpen(false)} invoice={pdfRow} workflowLog={workflowLog} />
      </Container>
    </Page>
  );
}
